import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserCircle2, UserPlus, Users, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, CITIES } from "@/lib/api";
import { useAuth, DEMO_USERS } from "@/lib/AuthContext";

export default function AuthModal({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const [name, setName] = useState("");
  const [city, setCity] = useState("Jakarta");
  const [contact, setContact] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const createProfile = async () => {
    if (name.trim().length < 2 || contact.trim().length < 5) {
      toast.error("Isi nama lengkap dan email/no HP yang valid.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/users", { name, city, contact });
      setUser({ id: data.id, name: data.name, city: data.city });
      toast.success(`Selamat datang, ${data.name}! Profil personalmu aktif — jam aksi mulai dari 0.`);
      onOpenChange(false);
      setName(""); setContact("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal membuat profil.");
    } finally {
      setSubmitting(false);
    }
  };

  const switchDemo = (d) => {
    setUser({ id: d.id, name: d.name, city: d.city });
    toast.success(`Beralih ke: ${d.label}`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="auth-modal" className="max-w-md rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UserCircle2 className="w-5 h-5 text-emerald-600" /> Masuk / Kelola Profil
          </DialogTitle>
          <DialogDescription>
            Sedang aktif: <span className="font-bold text-slate-700">{user?.name}</span> ({user?.city}).
            Data aksi tersimpan pada profil yang sedang aktif.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
            <UserPlus className="w-4 h-4 text-emerald-600" /> Buat Profil Sendiri
          </p>
          <Input data-testid="auth-name-input" placeholder="Nama lengkap" value={name}
            onChange={(e) => setName(e.target.value)} className="rounded-xl" aria-label="Nama lengkap" />
          <div className="grid grid-cols-2 gap-3">
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger data-testid="auth-city-select" className="rounded-xl" aria-label="Kota domisili">
                <SelectValue placeholder="Kota domisili" />
              </SelectTrigger>
              <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="auth-contact-input" placeholder="Email / No HP" value={contact}
              onChange={(e) => setContact(e.target.value)} className="rounded-xl" aria-label="Email atau nomor HP" />
          </div>
          <Button data-testid="auth-submit-btn" onClick={createProfile} disabled={submitting}
            className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
            {submitting ? "Memproses..." : "Masuk & Buat Profil Baru"}
          </Button>
        </div>

        <div className="flex items-center gap-3 my-1">
          <span className="flex-1 h-px bg-slate-200" />
          <span className="text-xs font-bold text-slate-400 uppercase">atau Quick Demo</span>
          <span className="flex-1 h-px bg-slate-200" />
        </div>

        <div className="space-y-2">
          {DEMO_USERS.map((d) => (
            <button
              key={d.id}
              data-testid={`demo-switch-${d.id}`}
              onClick={() => switchDemo(d)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border-2 text-left transition-colors ${
                user?.id === d.id ? "border-emerald-600 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"
              }`}
            >
              <Users className="w-5 h-5 text-slate-400 shrink-0" aria-hidden="true" />
              <span className="flex-1">
                <span className="block text-sm font-bold text-slate-800">{d.label}</span>
                <span className="block text-xs text-slate-500">{d.name} · {d.city}</span>
              </span>
              {user?.id === d.id && <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" aria-label="Profil aktif" />}
            </button>
          ))}
        </div>

        <p className="text-xs text-slate-400 leading-relaxed">
          Profil aktif disimpan di perangkat ini (localStorage). Pendaftaran aksi, klaim PIN kehadiran,
          dan rencana tersimpan akan terikat pada profil yang sedang aktif.
        </p>
      </DialogContent>
    </Dialog>
  );
}
